import { MoreHorizontal } from 'lucide-react'
import { Badge, type BadgeProps } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { Contact } from '@/lib/contacts/types'
import { useViewOnly } from '@/components/templates/app-shell'

const contactBadgeVariant: Record<Contact['contact_type'], BadgeProps['variant']> = {
  Person: 'person',
  Organization: 'organization',
}

// eslint-disable-next-line max-lines-per-function
export function ContactsTable({
  contacts,
  selectedIds,
  allVisibleSelected,
  onToggleContact,
  onToggleVisible,
  onOpenPerson,
  onOpenOrganization,
  onEditContact,
}: {
  contacts: Contact[]
  selectedIds: Set<number>
  allVisibleSelected: boolean
  onToggleContact: (contact: Contact) => void
  onToggleVisible: (checked: boolean) => void
  onOpenPerson: (contact: Contact) => void
  onOpenOrganization: (contact: Contact) => void
  onEditContact: (contact: Contact) => void
}) {
  const viewOnly = useViewOnly()
  return (
    <Table>
      <TableHeader>
        <TableRow>
          {!viewOnly && (
            <TableHead className="w-12">
              <Checkbox
                aria-label="Select visible Contacts"
                checked={allVisibleSelected}
                onCheckedChange={(checked) => onToggleVisible(checked === true)}
              />
            </TableHead>
          )}
          <TableHead>Name</TableHead>
          <TableHead>Email</TableHead>
          <TableHead>Type</TableHead>
          <TableHead>Marketing</TableHead>
          {!viewOnly && <TableHead className="w-12" aria-label="Contact actions" />}
        </TableRow>
      </TableHeader>
      <TableBody>
        {contacts.map((contact) => {
          const openContact = () =>
            contact.contact_type === 'Person' ? onOpenPerson(contact) : onOpenOrganization(contact)

          return (
            <TableRow
              key={contact.id}
              tabIndex={0}
              className="group cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
              onClick={openContact}
              onKeyDown={(event) => {
                if (event.key !== 'Enter' && event.key !== ' ') return

                event.preventDefault()
                openContact()
              }}
            >
              {!viewOnly && (
                <TableCell>
                  <Checkbox
                    aria-label={`Select ${contact.name}`}
                    checked={selectedIds.has(contact.id)}
                    onPointerDown={(event) => event.stopPropagation()}
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                    onCheckedChange={() => onToggleContact(contact)}
                  />
                </TableCell>
              )}
              <TableCell>
                <span className="text-foreground">{contact.name}</span>
                {contact.archived && (
                  <Badge variant="neutral" className="ml-2">
                    Archived
                  </Badge>
                )}
              </TableCell>
              <TableCell className="text-muted-foreground">{contact.email ?? '-'}</TableCell>
              <TableCell>
                <Badge variant={contactBadgeVariant[contact.contact_type]}>
                  {contact.contact_type}
                </Badge>
              </TableCell>
              <TableCell>
                <Badge variant={contact.marketing_opted_out ? 'neutral' : 'success'}>
                  {contact.marketing_opted_out ? 'Opted out' : 'Not opted out'}
                </Badge>
              </TableCell>
              {!viewOnly && (
                <TableCell
                  className="w-12 text-right"
                  onPointerDown={(event) => event.stopPropagation()}
                  onMouseDown={(event) => event.stopPropagation()}
                  onClick={(event) => event.stopPropagation()}
                  onKeyDown={(event) => event.stopPropagation()}
                >
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-8 w-8 px-0"
                        aria-label={`${contact.name} actions`}
                        onPointerDown={(event) => event.stopPropagation()}
                        onMouseDown={(event) => event.stopPropagation()}
                        onClick={(event) => event.stopPropagation()}
                        onKeyDown={(event) => event.stopPropagation()}
                      >
                        <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onSelect={() => onEditContact(contact)}>
                        Edit
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              )}
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}
